// The delta-v map's own state: what it is planning from, what it planned, and
// which destination the player has picked.
//
// No DOM and no three.js - `src/hudDvMap.ts` draws this and lane B2's Go acts
// on `selected()`. The map plans from the ship's LIVE heliocentric position and
// velocity, read off the `simApi` bus, and never from an assumed circular
// orbit: `src/sim/transfer.ts`'s own live-state path asks whether that vector
// sits inside a sphere of influence and takes the local radius and speed from
// the vector itself, so a ship on a closed orbit is planned as parked there
// only because its own state says so. What the departure then COSTS is the
// cheapest point of one revolution of that orbit rather than the point the ship
// is at when the panel opens; `src/sim/transfer.ts` states that rule and the
// survey's `departurePricing` says which case a plan answered.
//
// What the survey keeps re-solving, the PICK does not: the crossing armed when
// a row was picked is served in its place until the departure passes, the
// destination drops out, or the ship is at another body than the plan was
// solved from, and `src/sim/dvCorrection.ts` holds that same crossing for the
// row it prices. Giving one up puts `moved` on the state for the panel.
//
// The epoch comes straight from `gameClock()`, a module singleton, and the
// registry rows arrive through this factory's config off the `loadBodies()`
// main.ts already awaits - so opening the map costs no second fetch.

import {
  DEFAULT_PARKING_ALTITUDE_KM, TRANSFER_ASSUMPTIONS, dominantBodyAt, gmKm3S2,
  planTransfersSliced,
} from './transfer.ts';
import type {
  SurveyProgress, TransferOrigin, TransferPlan, TransferSurvey,
} from './transfer.ts';
import { simApi } from './api.ts';
import type { SimVec3 } from './api.ts';
import { arrivalAltitudeKm } from './arrivalOrbit.ts';
import type { ArrivalOrbit } from './arrivalOrbit.ts';
import { gameClock } from '../world/clock.ts';
import { heliocentric } from '../world/ephemeris.ts';
import type { Vec3 } from '../world/ephemeris.ts';
import type { BodyRow } from '../world/types.ts';

/** Where the map plans from: the ship's own state, or a circular parking orbit. */
export type DvMapOriginMode = 'live' | 'parking';

/**
 * Sim days the clock may move before the plan on screen is re-made.
 *
 * Set by what is displayed. Burns and time of flight come from each row's own
 * `a_au` and do not move with the epoch at all, so the only epoch-sensitive
 * figures are the window wait and the departure and arrival dates. The wait is
 * shown to a tenth of a day under ten days and to the whole day above it, so
 * half of the finest displayed digit is the point past which a character on
 * screen is wrong. A 400 km parking orbit's own period is 0.064 day, so this
 * also re-plans about once an orbit, which is the other thing that moves a
 * live origin.
 */
const EPOCH_REPLAN_DAYS = 0.05;

/** Metres a second of heliocentric speed change that re-plans: a burn. */
const SPEED_REPLAN_MPS = 5;

/**
 * Real milliseconds between two plans, however fast the clock runs. A survey
 * over the whole registry is a few milliseconds, and the epoch trigger alone
 * would fire about every four real seconds at a day a second; this is what
 * keeps a faster clock than that from turning it into a per-frame cost.
 */
const REPLAN_FLOOR_MS = 250;

/**
 * Real milliseconds of solving one frame may spend on a survey.
 *
 * A survey on the real orbits is 36,750 Lambert solves and about 620 ms, four
 * times the replan floor, so it is spent a slice at a time with the last survey
 * still on screen. The budget is time rather than a step count because the cost
 * of a step is not fixed: an inner-system row is a fifth of an outer-system one,
 * and a fixed count that suits Mars overruns on Pluto. The budget is read
 * before a step and not inside one, so a slice runs to the budget PLUS the step
 * it is already in; the two together are what the 10 ms bar holds.
 */
const SLICE_BUDGET_MS = 6;

/** Steps one slice may take whatever the clock says, so a machine whose
 *  `performance.now` is coarse still yields. Each step is a few dozen solves. */
const SLICE_STEP_CAP = 64;

/**
 * Days two departures may sit apart and still be the same crossing to a player.
 *
 * A re-solve that lands back on the window it already had has moved nobody, and
 * an hour is finer than the panel's own date cell can draw. Past it the trip is
 * a different one and the moved row says so.
 */
const MOVED_DEPARTURE_DAYS = 1 / 24;

interface OrbitSignature { readonly aKm: number; readonly e: number }

/** Tolerances under which a coasting orbit is still the same orbit. */
const ORBIT_SAME_REL = 0.01;
const ORBIT_SAME_ECC = 0.01;

function orbitAbout(
  host: BodyRow, rows: readonly BodyRow[], epochJd: number, positionKm: Vec3, velocityKm: Vec3,
): OrbitSignature | null {
  const h = heliocentric(host, rows, epochJd);
  const r = {
    x: positionKm.x - h.position.x, y: positionKm.y - h.position.y, z: positionKm.z - h.position.z,
  };
  const v = {
    x: velocityKm.x - h.velocity.x, y: velocityKm.y - h.velocity.y, z: velocityKm.z - h.velocity.z,
  };
  const gm = gmKm3S2(host);
  const rn = Math.hypot(r.x, r.y, r.z);
  const v2 = v.x * v.x + v.y * v.y + v.z * v.z;
  const inv = 2 / rn - v2 / gm;
  if (!(rn > 0) || !(gm > 0) || !Number.isFinite(inv) || inv <= 0) return null;
  const hx = r.y * v.z - r.z * v.y;
  const hy = r.z * v.x - r.x * v.z;
  const hz = r.x * v.y - r.y * v.x;
  const p = (hx * hx + hy * hy + hz * hz) / gm;
  const a = 1 / inv;
  const e2 = 1 - p / a;
  return { aKm: a, e: Math.sqrt(Math.max(0, e2)) };
}

function sameOrbit(a: OrbitSignature, b: OrbitSignature): boolean {
  return Math.abs(a.aKm - b.aKm) <= ORBIT_SAME_REL * Math.abs(a.aKm)
    && Math.abs(a.e - b.e) <= ORBIT_SAME_ECC;
}

export interface DvMapConfig {
  /** The registry, as main.ts holds it. Empty until `loadBodies()` resolves. */
  rows: () => readonly BodyRow[];
  /** TDB Julian date. The world clock unless a check hands in its own. */
  epochJd?: () => number;
  /** The ship's heliocentric metres. The `simApi` bus unless overridden. */
  shipAt?: () => SimVec3 | null;
  /** The ship's heliocentric metres a second, composed the same way. */
  shipVelocity?: () => SimVec3 | null;
  /** Circular orbit PARKING ORBIT mode departs from and every plan captures into. */
  parkingAltitudeKm?: number;
  /** The thrust the ship actually has, m/s2, which prices the departure as the
   *  burn it really is. Zero or absent leaves every burn an instant push. */
  shipAccelMps2?: () => number;
  /**
   * The control (`?dvmapstale=1`): plan once, from a parking orbit at the body
   * the ship booted over, and never plan again. This is a map that ignores
   * both the live state and the clock, which is the failure the `--dvmap`
   * arm's own readings have to catch.
   */
  stale?: boolean;
  /**
   * The control (`?dvmapzeromass=1`): every destination's `mass_kg` zeroed in
   * the context the map plans against, which is `dvmap-check.mjs --inject
   * zero-mass` surfacing through the displayed capture burn.
   */
  zeroMass?: boolean;
  /**
   * The control (`?dvmapburnnow=1`): every departure priced where the ship is
   * this instant, even on a closed orbit it comes round to a cheaper point of
   * before the window. That is the figure the panel used to show, and it swings
   * by a factor of sixteen round one revolution, so the closed-orbit readings
   * in the `--dvmap` arm have to go red on it.
   */
  burnNow?: boolean;
  /**
   * The control (`?dvmapplanej2000=1`): a moon's own orbit plane taken off its
   * J2000 element fit rather than off the epoch the plan is for, which is
   * where the first cut of the out-of-plane refusal read it. Phobos's plane
   * moves 1.1 degrees between J2000 and the game's boot date, so both the
   * tilt the planned row prints and the degrees an out-of-plane refusal names
   * come out wrong under it, and the `--dvmap` arm's moon leg has to go red.
   */
  planeFromJ2000?: boolean;
  /**
   * The control (`?dvmapunsliced=1`): the whole survey solved in the one call
   * that starts it, the way a survey ran before it was sliced. That is a 620 ms
   * frame, and it is what the `--dvmap` arm's slice bars have to go red on.
   */
  unsliced?: boolean;
  /**
   * The control (`?dvmapimpulsedeparture=1`): the departure priced as a single
   * instant push again, with nothing charged for the time the engine is open.
   * That is the figure the panel used to show, and a pilot who cut his burn on
   * it was still in orbit around the body he meant to leave, which is what the
   * escape reading in `transit-check.mjs --inject impulse-departure` catches.
   */
  impulseDeparture?: boolean;
  /**
   * True while the autopilot has the stick. The armed crossing is given up the
   * same way either way; this is what keeps the moved row quiet for a player
   * who is flying that crossing rather than being moved off one they missed.
   */
  underWay?: () => boolean;
  /**
   * The control (`?dvmapresolvesarmed=1`): the picked row re-solved under the
   * player every few hundred milliseconds, the way the map read before the pick
   * was held. A player who arms a window months out, waits for it and presses
   * GO then flies whichever crossing the map has walked on to - Earth to Mars
   * at boot offers a departure 698 days out and a different one 800 days later
   * by the time that day comes - which is what `--transit --inject
   * map-resolves-armed` has to catch.
   */
  resolveArmed?: boolean;
  /**
   * The control (`?dvmapholdonlyonsurvey=1`): the armed hold left to the surveys
   * alone, the way it read before a plan that bails with nothing to plan from
   * asked it. In PARKING ORBIT mode a ship outside every sphere of influence
   * has no parking orbit to leave from, so no survey lands for the whole cruise
   * and the hold is never asked again: a crossing armed from a parking orbit the
   * ship has left is only given up when the ship reaches another body, where the
   * panel names it as a crossing that left days ago. What `--transit --inject
   * hold-only-on-survey` has to catch.
   */
  holdOnlyOnSurvey?: boolean;
  /** The orbit every capture is priced into at boot: the player's remembered pick. */
  arrivalOrbit?: ArrivalOrbit;
  /** The control (`?goorbitignore=1`): every capture priced into LOW whatever was picked. */
  ignoreArrivalOrbit?: boolean;
}

/** What the map had to do when an armed crossing stopped being flyable, for the
 *  panel's own row. Null on the state while the armed crossing still stands. */
export interface DvMapMoved {
  readonly destinationName: string;
  /** TDB Julian date of the departure the player armed and did not make. */
  readonly armedDepartureJd: number;
  /** The departure they are offered in its place. */
  readonly departureJd: number;
  /** km/s the new crossing costs over the armed one; negative is cheaper. */
  readonly dvChangeKmS: number;
}

/** What the panel draws and what lane B2's Go reads. */
export interface DvMapState {
  readonly open: boolean;
  readonly mode: DvMapOriginMode;
  /** The survey on screen, or null while there is nothing to plan from. */
  readonly survey: TransferSurvey | null;
  /** Why there is no survey, in the player's own words, or null. */
  readonly reason: string | null;
  /** The picked destination's plan, or null. The crossing armed when the pick
   *  was made while that one can still be flown, not this survey's own answer. */
  readonly selected: TransferPlan | null;
  readonly selectedId: string | null;
  /** Set once the map has given up an armed crossing for a later one, and
   *  cleared by the next pick. Null while the armed crossing still stands. */
  readonly moved: DvMapMoved | null;
  /** TDB Julian date the survey on screen was planned at. */
  readonly epochJd: number;
  /** Where the plan leaves from, for the panel's own FROM line. */
  readonly originLabel: string;
  /** Height over that body's own radius, km, or null in deep space. */
  readonly originAltitudeKm: number | null;
  /** What the model leaves out, for the panel to show beside its numbers. */
  readonly assumptions: readonly string[];
  /** Milliseconds of real time the last complete survey took end to end,
   *  across every slice it was spent over. */
  readonly planMs: number;
  /** How many surveys have been made since boot. */
  readonly plans: number;
  /** Destinations whose row is still showing the last survey's price while
   *  this one is being solved. Empty when nothing is in flight. */
  readonly working: readonly string[];
  /** A survey is being solved right now. */
  readonly busy: boolean;
  /** The longest single slice of the last complete survey, ms: what the frame
   *  actually paid at its worst. */
  readonly sliceMaxMs: number;
  /** Frames the last complete survey was spent over. */
  readonly slices: number;
  /** Milliseconds of solving the last complete survey took, added up across
   *  its slices and not counting the frames in between. `sliceMaxMs` over this
   *  is the share of a survey one frame paid, which is what says the survey is
   *  spread rather than spent in one call whatever the machine's speed. */
  readonly solveMs: number;
  /** The orbit GO's arrival brakes into. */
  readonly arrivalOrbit: ArrivalOrbit;
  /** The pick changed and the survey priced for it has not landed yet, so the
   *  plans on screen still brake into the orbit picked before. */
  readonly arrivalRepricing: boolean;
}

export interface DvMap {
  /** Cheap per frame: returns at once while the panel is closed. */
  update: () => void;
  setOpen: (on: boolean) => void;
  toggle: () => void;
  isOpen: () => boolean;
  setMode: (mode: DvMapOriginMode) => void;
  /** Price every capture into this orbit from the next survey on, the picked crossing with them. */
  setArrivalOrbit: (orbit: ArrivalOrbit) => void;
  /** Pick a destination, or clear the pick. Lane B2's Go acts on this. */
  select: (destinationId: string | null) => void;
  /** The picked plan, or null. */
  selected: () => TransferPlan | null;
  /** The crossing the map gave up and the one in its place, or null. A fresh
   *  object every time it gives one up, so a reader that keeps the last one it
   *  saw can tell a new hand-off from the same one still on screen. */
  moved: () => DvMapMoved | null;
  state: () => DvMapState;
  /** Called on every change of `state()`. Returns its own unsubscribe. */
  subscribe: (listener: (state: DvMapState) => void) => () => void;
  /** Plan now, whatever the triggers say. */
  replan: () => void;
  dispose: () => void;
}

function toKm(v: SimVec3): Vec3 {
  return { x: v.x / 1000, y: v.y / 1000, z: v.z / 1000 };
}

/** Every destination's mass taken away, which is the zero-mass control. */
function zeroDestinationMass(
  rows: readonly BodyRow[],
  keepId: string | null,
): readonly BodyRow[] {
  return rows.map((row) => (
    row.id !== keepId && row.orbit !== null && !row.orbit.parent_relative
      ? { ...row, mass_kg: 0 }
      : row
  ));
}

export function createDvMap(config: DvMapConfig): DvMap {
  const epochOf = config.epochJd ?? ((): number => gameClock().jdTdb());
  const atOf = config.shipAt ?? ((): SimVec3 | null => simApi.ship.at());
  const velocityOf = config.shipVelocity ?? ((): SimVec3 | null => simApi.ship.velocity());
  const parkingAltitudeKm = config.parkingAltitudeKm ?? DEFAULT_PARKING_ALTITUDE_KM;
  const accelOf = config.shipAccelMps2 ?? ((): number => 0);
  const underWayOf = config.underWay ?? ((): boolean => false);
  const stale = config.stale === true;
  const resolveArmed = config.resolveArmed === true;
  const holdOnlyOnSurvey = config.holdOnlyOnSurvey === true;

  // The panel is in the rail from boot, so the map is planning from boot. Only
  // a fight takes it off screen (src/main.ts); the player's own way to put it
  // away is the minimize button every panel has.
  let open = true;
  let mode: DvMapOriginMode = 'live';
  let survey: TransferSurvey | null = null;
  let reason: string | null = null;
  /** The destination whose crossing was let go out here, for the FROM line's
   *  own reason: the row it belonged to is gone with the rest of the list, so
   *  this is the only place the panel can say it. Stands until a survey lands
   *  again, which is the next body the ship reaches. */
  let letGoName: string | null = null;
  let selectedId: string | null = null;
  let plannedEpochJd = 0;
  let plannedSpeedMps = 0;
  let originLabel = '';
  let originAltitudeKm: number | null = null;
  let planMs = 0;
  let plans = 0;
  let lastPlanRealMs = -Infinity;
  /** The survey being solved, or null when nothing is in flight. */
  let run: Generator<SurveyProgress, TransferSurvey, void> | null = null;
  let working: readonly string[] = [];
  let runStartedMs = 0;
  let runSliceMaxMs = 0;
  let runSlices = 0;
  let runSolveMs = 0;
  let sliceMaxMs = 0;
  let slices = 0;
  let solveMs = 0;
  /** What the FROM line needs once the survey in flight lands: read when it was
   *  started, since the ship has moved on by the time it does. */
  let pendingOrigin: {
    positionKm: Vec3; rows: readonly BodyRow[]; epochJd: number; parked: boolean;
    orbit: OrbitSignature | null;
  } | null = null;
  let surveyOrbit: OrbitSignature | null = null;
  let armedOrbit: OrbitSignature | null = null;
  /** The body PARKING ORBIT mode uses once the stale control has frozen it. */
  let staleBodyId: string | null = null;
  let arrivalOrbit: ArrivalOrbit = config.arrivalOrbit ?? 'low';
  /** The orbit the survey in flight prices its captures into. */
  let runOrbit: ArrivalOrbit = arrivalOrbit;
  /** Set by a new arrival pick until a survey priced for it lands; the armed
   *  crossing is re-priced then rather than held at the old orbit's price. */
  let repriceArmed = false;
  /**
   * The crossing the pick is armed on, and which destination armed it.
   *
   * The survey is re-made every few hundred milliseconds and its next window
   * walks forward with the clock: Earth to Mars at boot departs 698 days out,
   * and by the day that window comes round the same solve offers one 800 days
   * later and cheaper. A player who picks a row is picking THAT crossing, so it
   * is taken once and served in place of every later survey's answer for it
   * until the departure passes, the destination drops out of the survey, or
   * the ship is at another body than the plan was solved from.
   */
  let armed: TransferPlan | null = null;
  let armedId: string | null = null;
  /** The survey's `originBodyId` at the moment `armed` was taken, so a ship
   *  that has gone to another body is caught even before its departure passes.
   *  A survey names no origin body at all while the ship is inside no sphere
   *  of influence, which is most of a coast, and that is not another body. */
  let armedOriginBodyId: string | null = null;
  /** True where the pick was the player's; false for the row `finish` takes
   *  when there is none, which nobody armed and nothing is told about. */
  let armedByPick = false;
  let moved: DvMapMoved | null = null;
  const listeners = new Set<(state: DvMapState) => void>();

  /** A survey with the armed crossing in place of its own answer for that
   *  destination, the wait re-counted from this survey's epoch so the row still
   *  counts down to a departure that does not move. */
  function withArmed(done: TransferSurvey): TransferSurvey {
    const plan = armed;
    if (plan === null) return done;
    if (!done.plans.some((p) => p.destinationId === plan.destinationId)) return done;
    const shown = { ...plan, windowWaitDays: plan.departureEpochJd - done.epochJd };
    const plans = done.plans
      .map((p) => (p.destinationId === plan.destinationId ? shown : p))
      .sort((a, b) => a.totalDvKmS - b.totalDvKmS);
    return { ...done, plans };
  }

  function snapshot(): DvMapState {
    const shown = survey === null ? null : withArmed(survey);
    const selected = selectedId === null || shown === null
      ? null
      : shown.plans.find((plan) => plan.destinationId === selectedId) ?? null;
    return {
      open, mode, survey: shown, reason, selected, selectedId, moved,
      epochJd: plannedEpochJd, originLabel, originAltitudeKm,
      assumptions: shown?.assumptions ?? TRANSFER_ASSUMPTIONS,
      planMs, plans, working, busy: run !== null, sliceMaxMs, slices, solveMs,
      arrivalOrbit, arrivalRepricing: repriceArmed,
    };
  }

  /** Take the armed crossing where there is none, hold it while it can still be
   *  flown, and give it up once it cannot: the clock has reached its departure,
   *  the survey no longer offers that destination at all, or the ship is at
   *  another named body than the one the plan was solved from. A null survey is
   *  a plan that bailed with nothing to plan from, which is the one case no
   *  `finish()` will come and ask about. */
  function holdArmed(done: TransferSurvey | null, orbit: OrbitSignature | null = null): void {
    const id = selectedId;
    if (resolveArmed || id === null) {
      armed = null;
      armedId = null;
      armedOriginBodyId = null;
      return;
    }
    const fresh = done?.plans.find((p) => p.destinationId === id) ?? null;
    if (done === null || fresh === null) {
      armed = null;
      armedId = null;
      armedOriginBodyId = null;
      return;
    }
    const was = armedId === id ? armed : null;
    // A ship that is at ANOTHER body than the plan assumed - flown there, or
    // reparked there - can no longer fly that burn, whatever its departure
    // says, so this overrides the "still time left" return below. Both origins
    // have to be named for that: a survey names none between spheres of
    // influence, and the coast the crossing was armed for is not a move.
    const originMoved = was !== null && armedOriginBodyId !== null
      && done.originBodyId !== null && armedOriginBodyId !== done.originBodyId;
    // The price is for the orbit it was armed from; a ship that has changed
    // orbit at the same body and is not flying the plan is quoted its own.
    const orbitChanged = was !== null && !originMoved && !underWayOf()
      && armedOrbit !== null && orbit !== null
      && !sameOrbit(armedOrbit, orbit);
    if (orbitChanged) {
      armed = fresh;
      armedOrbit = orbit;
      return;
    }
    if (was !== null && !originMoved && !repriceArmed && done.epochJd < was.departureEpochJd) return;
    // A window that passed is a hand-off where the ship is still somewhere it
    // could have left from. Between spheres of influence the survey names no
    // origin body, and the ship there is mid-cruise on a crossing it departed
    // on: re-pricing it every few hundred ms is the panel following the ship,
    // not a trip going without them, and a row told it was handed on there
    // drops the plan the fix is being flown against.
    const windowWentBy = was !== null && done.originBodyId !== null
      && fresh.departureEpochJd - was.departureEpochJd > MOVED_DEPARTURE_DAYS;
    // The autopilot flying this crossing out of the body's sphere is the window
    // being taken, not missed; the body's next window would be held all coast.
    if (windowWentBy && underWayOf()) return;
    if (was !== null && armedByPick && !underWayOf()
      && (originMoved || (windowWentBy && !repriceArmed))) {
      moved = {
        destinationName: fresh.destinationName,
        armedDepartureJd: was.departureEpochJd,
        departureJd: fresh.departureEpochJd,
        dvChangeKmS: fresh.totalDvKmS - was.totalDvKmS,
      };
    }
    armed = fresh;
    armedId = id;
    armedOriginBodyId = done.originBodyId;
    armedOrbit = orbit;
  }

  function emit(): void {
    const state = snapshot();
    for (const listener of listeners) listener(state);
  }

  function speedMps(): number {
    const v = velocityOf();
    return v === null ? 0 : Math.hypot(v.x, v.y, v.z);
  }

  /** Height over the host body's own radius, for the FROM line. */
  function altitudeKmAt(
    positionKm: Vec3,
    hostId: string | null,
    rows: readonly BodyRow[],
    epochJd: number,
  ): number | null {
    if (hostId === null) return null;
    const row = rows.find((r) => r.id === hostId);
    if (row === undefined) return null;
    const centre = heliocentric(row, rows, epochJd).position;
    const distanceKm = Math.hypot(
      positionKm.x - centre.x, positionKm.y - centre.y, positionKm.z - centre.z,
    );
    return Math.max(0, distanceKm - row.radius_m / 1000);
  }

  /** Start a survey. It is solved a slice at a time from `update()`; the one on
   *  screen stays there until this one lands. The epoch is read ONCE, here: a
   *  survey that re-read the clock between slices would price its first rows
   *  and its last at different dates. */
  function plan(): void {
    const rows = config.rows();
    const epochJd = epochOf();
    run = null;
    working = [];
    plannedEpochJd = epochJd;
    plannedSpeedMps = speedMps();
    lastPlanRealMs = performance.now();
    plans++;

    if (rows.length === 0) {
      survey = null;
      reason = 'the body registry has not loaded yet';
      originLabel = '';
      originAltitudeKm = null;
      return;
    }
    const at = atOf();
    if (at === null) {
      survey = null;
      reason = 'the ship has no position yet';
      originLabel = '';
      originAltitudeKm = null;
      return;
    }
    const velocity = velocityOf();
    if (velocity === null) {
      survey = null;
      reason = 'the ship has no velocity yet';
      originLabel = '';
      originAltitudeKm = null;
      return;
    }

    const positionKm = toKm(at);
    const hostCtx = { rows, epochJd, parkingAltitudeKm };
    const host = dominantBodyAt(positionKm, hostCtx);
    if (stale && staleBodyId === null) staleBodyId = host?.id ?? null;
    const parkedId = stale ? staleBodyId : host?.id ?? null;

    let origin: TransferOrigin;
    if (mode === 'parking' || stale) {
      if (parkedId === null) {
        survey = null;
        reason = 'nothing is pulling here, so there is no parking orbit to leave from';
        originLabel = 'deep space';
        originAltitudeKm = null;
        // No survey is coming to ask the hold anything, and a crossing armed
        // from a parking orbit the ship has left cannot be flown, so it is
        // asked here rather than at the next body the ship reaches.
        if (!holdOnlyOnSurvey) {
          if (armed !== null && armedByPick) letGoName = armed.destinationName;
          holdArmed(null);
          // A line about a crossing handed on to another one outlives the hold
          // it was drawn for otherwise: nothing else clears it until the next
          // pick, and the next body the ship reaches would draw it again about
          // a crossing this bail has already let go.
          moved = null;
        }
        // Letting it go silently is the one way a crossing goes without the
        // panel saying so. There is nothing to move on to out here, so the
        // moved row cannot carry it and the FROM line's reason does.
        if (letGoName !== null) {
          reason += `, and the crossing to ${letGoName} you picked is let go here`;
        }
        return;
      }
      origin = { kind: 'parking', bodyId: parkedId, altitudeKm: parkingAltitudeKm };
    } else {
      origin = { kind: 'state', state: { position: positionKm, velocity: toKm(velocity) } };
    }

    const ctxRows = config.zeroMass === true
      ? zeroDestinationMass(rows, mode === 'parking' || stale ? parkedId : host?.id ?? null)
      : rows;
    runOrbit = arrivalOrbit;
    const priceOrbit = config.ignoreArrivalOrbit === true ? 'low' : runOrbit;
    const highKm = new Map<string, number>();
    run = planTransfersSliced(origin, {
      rows: ctxRows,
      epochJd,
      parkingAltitudeKm,
      captureAltitudeKm: priceOrbit === 'low' ? undefined : (row: BodyRow): number => {
        let km = highKm.get(row.id);
        if (km === undefined) {
          km = arrivalAltitudeKm(row, rows, priceOrbit, parkingAltitudeKm);
          highKm.set(row.id, km);
        }
        return km;
      },
      departureAtShipPoint: config.burnNow === true,
      planeFromJ2000: config.planeFromJ2000 === true,
      shipAccelMps2: config.impulseDeparture === true ? undefined : accelOf(),
    });
    runStartedMs = performance.now();
    runSliceMaxMs = 0;
    runSlices = 0;
    runSolveMs = 0;
    const parked = mode === 'parking' || stale;
    pendingOrigin = {
      positionKm, rows, epochJd, parked,
      orbit: parked || host === null ? null : orbitAbout(host, rows, epochJd, positionKm, toKm(velocity)),
    };
  }

  /** The survey on screen with each landed row swapped in, so the list re-sorts
   *  as rows land and a row not yet re-priced keeps the price it had. */
  function merge(progress: SurveyProgress): boolean {
    const shown = survey;
    if (shown === null) return false;
    const pending = new Set(progress.pendingIds);
    const held = shown.plans.filter((p) => pending.has(p.destinationId));
    const next = [...progress.plans, ...held].sort((a, b) => a.totalDvKmS - b.totalDvKmS);
    const nextWorking = held.map((p) => p.destinationId);
    const changed = nextWorking.length !== working.length;
    survey = { ...shown, plans: next };
    working = nextWorking;
    return changed;
  }

  function finish(done: TransferSurvey): void {
    const origin = pendingOrigin;
    survey = done;
    working = [];
    planMs = performance.now() - runStartedMs;
    sliceMaxMs = runSliceMaxMs;
    slices = runSlices;
    solveMs = runSolveMs;
    run = null;
    reason = done.plans.length === 0 ? 'nothing here is reachable' : null;
    letGoName = null;
    originLabel = done.originLabel;
    originAltitudeKm = origin === null
      ? null
      : origin.parked
        ? parkingAltitudeKm
        : altitudeKmAt(origin.positionKm, done.originBodyId, origin.rows, origin.epochJd);
    if (selectedId !== null
      && !done.plans.some((p) => p.destinationId === selectedId)) {
      const next = done.plans[0] ?? null;
      if (armedId === selectedId && armed !== null && armedByPick && !underWayOf()
        && next !== null) {
        moved = {
          destinationName: next.destinationName,
          armedDepartureJd: armed.departureEpochJd,
          departureJd: next.departureEpochJd,
          dvChangeKmS: next.totalDvKmS - armed.totalDvKmS,
        };
      } else {
        moved = null;
      }
      selectedId = next?.destinationId ?? null;
      armedByPick = false;
    }
    if (selectedId === null) {
      selectedId = done.plans[0]?.destinationId ?? null;
      armedByPick = false;
    }
    surveyOrbit = origin?.orbit ?? null;
    holdArmed(done, surveyOrbit);
    if (runOrbit === arrivalOrbit) repriceArmed = false;
  }

  /** Spend one frame's budget on the survey in flight. Returns true where the
   *  panel has something new to draw. */
  function slice(budgetMs: number, stepCap: number = SLICE_STEP_CAP): boolean {
    const active = run;
    if (active === null) return false;
    const started = performance.now();
    runSlices++;
    let step = active.next();
    let steps = 1;
    while (!step.done && steps < stepCap && performance.now() - started < budgetMs) {
      step = active.next();
      steps++;
    }
    const spentMs = performance.now() - started;
    if (spentMs > runSliceMaxMs) runSliceMaxMs = spentMs;
    runSolveMs += spentMs;
    if (step.done) {
      finish(step.value);
      return true;
    }
    return merge(step.value);
  }

  /** The whole survey in one bite, which is what the control has to look like:
   *  no budget and no step cap, so it is recorded as the single slice it is. */
  function driveToEnd(): void {
    while (run !== null) slice(Infinity, Infinity);
  }

  /** Plan now, whatever the triggers say. The survey is still spent a slice at
   *  a time from `update()`; this only starts it. The frozen map stays frozen:
   *  a control that could be asked to re-plan would not be one. */
  function replan(): void {
    if (stale && survey !== null) return;
    plan();
    emit();
  }

  return {
    update(): void {
      if (!open) return;
      // A survey in flight gets the frame, and nothing else starts one until it
      // lands. `?dvmapunsliced=1` spends the whole thing in this one call.
      if (run !== null) {
        if (config.unsliced === true) {
          driveToEnd();
          emit();
          return;
        }
        if (slice(SLICE_BUDGET_MS)) emit();
        return;
      }
      if (performance.now() - lastPlanRealMs < REPLAN_FLOOR_MS) return;
      // A plan that could not be made is retried as soon as there is something
      // to plan from. The map now starts planning at boot, where the registry
      // is still loading and the ship has no state yet, and the epoch trigger
      // below would leave that first failure on screen for an hour of sim time.
      // `stale` freezes the map AFTER this first plan, not before it: the map
      // boots open now, ahead of the registry, so a stale map that skipped
      // this retry would never plan at all and would sit on "not loaded yet"
      // rather than the one frozen plan `?dvmapstale=1` exists to hold still.
      if (survey === null) {
        if (config.rows().length === 0 || atOf() === null || velocityOf() === null) return;
        plan();
        emit();
        return;
      }
      if (stale) return;
      const epochMoved = Math.abs(epochOf() - plannedEpochJd) > EPOCH_REPLAN_DAYS;
      const burned = Math.abs(speedMps() - plannedSpeedMps) > SPEED_REPLAN_MPS;
      if (!epochMoved && !burned) return;
      plan();
    },
    setOpen(on: boolean): void {
      if (on === open) return;
      open = on;
      if (on) plan();
      emit();
    },
    toggle(): void {
      this.setOpen(!open);
    },
    isOpen(): boolean {
      return open;
    },
    setMode(next: DvMapOriginMode): void {
      if (next === mode) return;
      mode = next;
      // Asking for the other origin is asking to be re-priced, so the crossing
      // armed against the one before it is let go rather than held through.
      armed = null;
      armedId = null;
      armedOriginBodyId = null;
      moved = null;
      letGoName = null;
      if (open) plan();
      emit();
    },
    setArrivalOrbit(next: ArrivalOrbit): void {
      if (next === arrivalOrbit) return;
      arrivalOrbit = next;
      repriceArmed = true;
      if (open) plan();
      emit();
    },
    select(destinationId: string | null): void {
      if (destinationId === selectedId) return;
      selectedId = destinationId;
      moved = null;
      letGoName = null;
      armed = null;
      armedId = null;
      armedOriginBodyId = null;
      armedByPick = destinationId !== null;
      // The crossing is armed off the survey on screen, which is the one the
      // player read before they picked the row, rather than at the next survey.
      if (destinationId !== null && survey !== null && !resolveArmed) {
        const pick = survey.plans.find((p) => p.destinationId === destinationId) ?? null;
        if (pick !== null) {
          armed = pick;
          armedId = destinationId;
          armedOriginBodyId = survey.originBodyId;
          armedOrbit = surveyOrbit;
        }
      }
      emit();
    },
    selected(): TransferPlan | null {
      return snapshot().selected;
    },
    moved: (): DvMapMoved | null => moved,
    state: snapshot,
    subscribe(listener: (state: DvMapState) => void): () => void {
      listeners.add(listener);
      listener(snapshot());
      return () => { listeners.delete(listener); };
    },
    replan,
    dispose(): void {
      listeners.clear();
      survey = null;
      run = null;
      working = [];
      armed = null;
      armedId = null;
      armedOriginBodyId = null;
      moved = null;
      open = false;
    },
  };
}
